import { useState, useEffect } from 'react';
import explainerPdf from '@/assets/BEA 040726 ETHANOL EXPLAINER.pdf';
import logoHeader from '@/assets/logo_header.png';

interface AdvisoryModalProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

export const AdvisoryModal = ({ forceOpen = false, onClose }: AdvisoryModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if opened before
    const hasSeenAdvisory = localStorage.getItem('bea_statutory_advisory_seen');
    if (!hasSeenAdvisory || forceOpen) {
      // Small delay for smooth entry after DOM mount
      const timer = setTimeout(() => setIsOpen(true), 400);
      return () => clearTimeout(timer);
    }
  }, [forceOpen]);

  useEffect(() => {
    const handleCustomOpen = () => setIsOpen(true);
    window.addEventListener('open-bea-advisory', handleCustomOpen);
    return () => window.removeEventListener('open-bea-advisory', handleCustomOpen);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('bea_statutory_advisory_seen', 'true');
    setIsOpen(false);
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/75 backdrop-blur-md animate-fade-in">
      <div 
        className="relative w-full max-w-4xl h-[88vh] max-h-[850px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden my-auto animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-3 sm:p-4 bg-surface border-b border-gray-200/80 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <img src={logoHeader} alt="BEA Logo" className="h-10 sm:h-12 w-auto object-contain shrink-0" />
            <div>
              <h2 className="text-base sm:text-lg font-bold text-ink leading-snug">
                Bihar Ethanol Association (BEA)
              </h2>
              <p className="text-xs text-ink-muted">
                Statutory Media Advisory & Technical Clarification on E20 Fuel
              </p>
            </div>
          </div>
        </div>

        {/* Single Scroll PDF Container */}
        <div className="flex-1 w-full bg-slate-100 p-2 sm:p-3 overflow-hidden">
          <iframe
            src={`${explainerPdf}#toolbar=0&navpanes=0&scrollbar=1`}
            className="w-full h-full rounded-xl border border-gray-200 bg-white"
            title="BEA Statutory Advisory PDF Document"
          />
        </div>

        {/* Modal Footer */}
        <div className="p-3 sm:p-4 bg-surface border-t border-gray-200/80 flex items-center justify-end gap-3 shrink-0">
          <button
            onClick={handleDismiss}
            className="px-6 py-2.5 rounded-lg bg-bteal text-white text-xs font-semibold hover:bg-bteal-dark transition-colors shadow-sm cursor-pointer whitespace-nowrap"
          >
            I Understand &amp; Close
          </button>
        </div>
      </div>
    </div>
  );
};
